import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import SearchForm from '../components/SearchForm'
import SearchResults from '../components/SearchResults'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import { SearchResponse } from '../types'
import { SearchService } from '../services/searchService'

export default function ResultsPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const query = searchParams.get('q') || ''
  
  const [searchResponse, setSearchResponse] = useState<SearchResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (query) {
      performSearch(query)
    }
  }, [query])

  const performSearch = async (searchQuery: string) => {
    if (!searchQuery.trim()) return
    
    setIsLoading(true)
    setError(null)
    
    try {
      const response = await SearchService.search(searchQuery)
      setSearchResponse(response)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed')
      setSearchResponse(null)
    } finally {
      setIsLoading(false)
    }
  }

  const handleNewSearch = (newQuery: string) => {
    navigate(`/search?q=${encodeURIComponent(newQuery.trim())}`)
  }

  const handleBackToHome = () => {
    navigate('/')
  }

  if (!query) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">No Search Query</h1>
          <p className="text-gray-600 mb-6">Please enter a search query to get started.</p>
          <button
            onClick={handleBackToHome}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Back to Search
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Compact Search Form */}
      <div className="bg-white border-b px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={handleBackToHome}
            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
          >
            ← Back
          </button>
          
          <div className="flex-1 max-w-2xl mx-8">
            <SearchForm
              onSearch={handleNewSearch}
              initialValue={query}
              placeholder="Refine your search..."
              compact={true}
            />
          </div>
        </div>
      </div>

      {/* Results */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {isLoading && <LoadingSpinner />}
        
        {error && (
          <ErrorMessage 
            message={error}
            onRetry={() => performSearch(query)}
          />
        )}
        
        {!isLoading && !error && searchResponse && (
          <div className="space-y-4">
            <SearchResults results={searchResponse.results} />
            
            {searchResponse.results.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No results found for your query.</p>
                <p className="text-gray-400 mt-2">Try rephrasing your question or using different keywords.</p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  )
}
